/**
 * useAbExperiment — tiny hook around the backend A/B harness.
 *
 * Usage:
 *   const { variant, track } = useAbExperiment('onevoice_radar_save_rate');
 *   // variant === 'control' | 'taste_weight'
 *   track('save', { track_id: t.id });
 *
 * Design:
 *   - Anonymous users get a sticky `ab_anon_id` persisted in localStorage
 *     so the same browser keeps the same variant across visits.
 *   - Variant is cached in-memory per hook instance and in localStorage
 *     keyed on `ab::{experiment_key}::{anonOrUserId}`.
 *   - All network calls are fire-and-forget; failures never throw.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function getAnonId() {
  try {
    let id = localStorage.getItem('ab_anon_id');
    if (!id) {
      id = 'a_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem('ab_anon_id', id);
    }
    return id;
  } catch {
    return 'a_ephemeral';
  }
}

export function useAbExperiment(experimentKey, { preAssignedVariant } = {}) {
  const { token, user } = useAuth();
  const [variant, setVariant] = useState(preAssignedVariant || null);
  const anonIdRef = useRef(getAnonId());

  // Fetch/cache the variant
  useEffect(() => {
    if (preAssignedVariant) {
      setVariant(preAssignedVariant);
      return;
    }
    const cacheKey = `ab::${experimentKey}::${user?.id || anonIdRef.current}`;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) { setVariant(cached); return; }
    } catch { /* ignore */ }
    let cancelled = false;
    axios.get(`${API}/ab/assign/${experimentKey}`, {
      params: { anon_id: anonIdRef.current },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then((r) => {
      if (cancelled) return;
      const v = r.data?.variant || 'control';
      setVariant(v);
      try { localStorage.setItem(cacheKey, v); } catch { /* ignore */ }
    }).catch(() => { if (!cancelled) setVariant('control'); });
    return () => { cancelled = true; };
  }, [experimentKey, preAssignedVariant, token, user?.id]);

  const track = useCallback((event, extra = {}) => {
    if (!variant) return;
    const payload = {
      experiment_key: experimentKey,
      variant,
      event,
      subject_id: extra.subject_id || extra.track_id || null,
      metadata: extra.metadata || null,
      anon_id: user?.id ? null : anonIdRef.current,
    };
    axios.post(`${API}/ab/track`, payload, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).catch(() => { /* fire-and-forget */ });
  }, [experimentKey, variant, token, user?.id]);

  return { variant: variant || 'control', track };
}
